import React from 'react';
import { DollarSign, CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import { Transaction } from '@/lib/portal-api-client';

interface PaymentHistoryTableProps {
  transactions: Transaction[];
}

export function PaymentHistoryTable({ transactions }: PaymentHistoryTableProps) {
  const statusConfig = {
    completed: {
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      label: 'Completed',
    },
    succeeded: {
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      label: 'Completed',
    },
    pending: {
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
      label: 'Pending',
    },
    failed: {
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      label: 'Failed',
    },
    refunded: {
      icon: RefreshCw,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      label: 'Refunded',
    },
  };

  const typeLabels = {
    payment: 'Payment',
    refund: 'Refund',
    adjustment: 'Adjustment',
    failed_payment: 'Failed Payment',
  };

  const isPaidStatus = (status: string) => {
    const s = String(status || '').toLowerCase();
    return s === 'completed' || s === 'succeeded';
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200 p-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Payment History</h2>
            <p className="text-sm text-gray-600">
              {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      {transactions.length === 0 ? (
        <div className="p-12 text-center">
          <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No transactions yet</p>
          <p className="text-sm text-gray-500 mt-1">Your payment history will appear here</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {transactions.map((transaction) => {
                const status = statusConfig[transaction.status] || statusConfig.completed;
                const StatusIcon = status.icon;
                const typeLabel = typeLabels[transaction.transaction_type] || transaction.transaction_type;
                const description = String(transaction.description || '').trim();
                const isRefund = transaction.transaction_type === 'refund';

                return (
                  <tr key={transaction.id} className="hover:bg-gray-50 transition-colors">
                    {/* Date */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {new Date(transaction.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(transaction.created_at).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </div>
                    </td>

                    {/* Description */}
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{description || typeLabel}</div>
                      {transaction.billing_period_start && transaction.billing_period_end && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {new Date(transaction.billing_period_start).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}{' '}
                          -{' '}
                          {new Date(transaction.billing_period_end).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </div>
                      )}
                      {transaction.stax_transaction_id && (
                        <div className="text-xs text-gray-400 mt-0.5 font-mono">
                          ID: {transaction.stax_transaction_id.slice(0, 12)}...
                        </div>
                      )}
                    </td>

                    {/* Amount */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className={`text-sm font-bold ${
                          transaction.transaction_type === 'refund' 
                            ? 'text-blue-600' 
                            : transaction.transaction_type === 'payment'
                            ? 'text-gray-900'
                            : 'text-gray-700'
                        }`}
                      >
                        {isRefund ? '-' : ''}${(transaction.amount_cents / 100).toFixed(2)}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${status.bgColor} ${status.color}`}
                      >
                        <StatusIcon className="w-3.5 h-3.5" />
                        {status.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary Footer */}
      {transactions.length > 0 && (
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">
              Total paid:{' '}
              <span className="font-bold text-gray-900">
                $
                {(
                  transactions
                    .filter(
                      (t) =>
                        t.transaction_type === 'payment' &&
                        isPaidStatus(t.status)
                    )
                    .reduce((sum, t) => sum + t.amount_cents, 0) / 100
                ).toFixed(2)}
              </span>
            </span>
            {transactions.some((t) => t.transaction_type === 'refund') && (
              <span className="text-gray-600">
                Total refunded:{' '}
                <span className="font-bold text-blue-600">
                  $
                  {(
                    transactions
                      .filter((t) => t.transaction_type === 'refund')
                      .reduce((sum, t) => sum + t.amount_cents, 0) / 100
                  ).toFixed(2)}
                </span>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
